import {
  makeObservable,
  observable,
  runInAction,
  action,
  computed,
} from "mobx";
import { webAPI } from "../api/Api";
import { StatusInfo, WithBooleanFlag, PerPageSettings } from "./core";
import Constants from "../constants/Common";
import DefectFormApi from "../api/DefectForm";
import DispatchApi from "../api/Dispatcher";
import FileApi from "../api/Files";
import allDefectDataStore from "./allDefectData";
import agreementDefectDataStore from "./agreementDefectData";
import plannedDefectDataStore from "./plannedDefectData";
import agreementPlanedDefectDataStore from "./agreementPlanedDefectData";
import { downloadRawFileFromServer } from "../api/helpers";
import { message } from "antd";
import { history } from "..";
import {
  AGREED_PLANNED,
  CONSISTENT_DEFECTS,
  REGISTER_DEFECTS,
  REGISTER_PLANNED,
} from "../components/DefectValidator/constants";
import { timeToLocal } from "../utils/helpers/data.helpers";

const MAX_CARDS_COUNT = 6;

export interface IDefectCard {
  name: string;
  key: string;
  closable: boolean;
  isLoading: boolean;
  planned: boolean;
  data: any;
}

class DefectCards {
  cards: Array<IDefectCard> = [];
  activeTab = "";
  defectNotifySettingsAreLoading = new WithBooleanFlag();
  defectNotifyFormIsOpened = new WithBooleanFlag(false);
  defectNotifyFormIsSending = new WithBooleanFlag(false);
  defectNotifyStatus = new StatusInfo();
  defectNotifySettings = null;
  defectNotifyText = "";
  defectNotifyTextShort = "";
  defectRejectFormIsOpened = new WithBooleanFlag(false);
  defectRejectFormIsSending = new WithBooleanFlag(false);
  defectRejectStatus = new StatusInfo();
  defectRemoveFormIsOpened = new WithBooleanFlag(false);
  defectRemoveFormIsSending = new WithBooleanFlag(false);
  defectRemoveStatus = new StatusInfo();
  defectInfoIsLoading = new WithBooleanFlag(true);
  defectInfo = null;
  defectFormIsSending = new WithBooleanFlag(false);
  saveDefectStatus = new StatusInfo();
  notificationsListsAreLoading = new WithBooleanFlag();
  notificationsListsDataPaginator = new PerPageSettings();
  notificationsListsData = [];
  defectToMkaIsSending = new WithBooleanFlag(false);
  sendDefectStatus = new StatusInfo();
  sendDefectToMkaIsOpened = new WithBooleanFlag(false);
  defectToMkaError: string[] = [];
  defectAgreedIsSending = new WithBooleanFlag(false);
  allDefectData = allDefectDataStore;
  agreementDefectData = agreementDefectDataStore;
  plannedDefectData = plannedDefectDataStore;
  agreementPlanedDefectData = agreementPlanedDefectDataStore;
  defectUploadFiles: Array<any> = [];
  saveDefectFileIsOpened = new WithBooleanFlag(false);
  saveDefectError = "";
  defectsIds = [];

  constructor() {
    makeObservable(
      this,
      {
        cards: observable,
        plannedCards: computed,
        nonPlannedCards: computed,
        activeTab: observable,
        defectNotifySettings: observable,
        defectNotifyText: observable,
        defectNotifyTextShort: observable,
        setActiveTab: action,
        addCard: action,
        editCard: action,
        removeCard: action,
        getNotificationSettings: action,
        getNotificationTemplateText: action,
        sendDefectNotification: action,
        closeNotifyForm: action,
        sendDefectRejection: action,
        closeRejectForm: action,
        sendDefectRemove: action,
        closeRemoveForm: action,
        saveDefect: action,
        defectInfo: observable,
        getNotificationsLists: action,
        notificationsListsData: observable,
        sendDefectToMka: action,
        closeDefectToMkaForm: action,
        defectToMkaError: observable,
        sendDefectAgreed: action,
        sendDefectAgreedAll: action,
        getDefectUploadFile: action,
        defectUploadFiles: observable,
        saveDefectError: observable,
        closeSaveDefect: action,
        defectsIds: observable,
        setDefectsIds: action,
        filterDefectsIds: action,
        clearDefectsIds: action,
      },
      {
        deep: true,
        autoBind: true,
      },
    );
  }

  setActiveTab = (id: string) => {
    this.activeTab = id;
  };

  async addCard(id: string) {
    const res = await this.getDefectInfoAdded(id);
    if (!res?.data) return;
    //debugger;
    const isPlanned = res.data?.eventTypeId === 3;
    const key = `${isPlanned ? "P" : "D"}${id}`;
    const keyRU = `${isPlanned ? "П № " : "Д № "}${id}`;
    const idx = this.cards.findIndex((el) => el.key === key);

    if (idx !== -1) {
      this.activeTab = this.cards[idx].key;
      //history.replace(`/sp/reestr/${id}`);
      return;
    }

    // TODO тут можно кеширование прикрутить, где-то вне табов хранить загруженное, и в случае активации таба, подсовывать
    // fetch data
    const card: IDefectCard = {
      name: keyRU,
      key: key,
      closable: true,
      isLoading: true,
      data: { ...res.data },
      planned: isPlanned,
    };

    runInAction(() => {
      this.cards.push({
        ...card,
        isLoading: false,
      });

      const activeIdx = this.cards.length - 1;
      this.activeTab = this.cards[activeIdx].key;

      const cardsPlanned = this.cards.filter((i) => i.planned == true);
      const cardsDefect = this.cards.filter((i) => i.planned == false);

      if (cardsPlanned.length == MAX_CARDS_COUNT) {
        this.cards = cardsPlanned.splice(1).concat(cardsDefect);
      }
      if (cardsDefect.length == MAX_CARDS_COUNT) {
        this.cards = cardsDefect.splice(1).concat(cardsPlanned);
      }
      //history.replace(`/sp/reestr/${id}`);
    });
  }

  async editCard(id: number) {
    const idx = this.cards.findIndex((el) => id === el.data?.id);

    if (idx === -1) {
      return;
    }

    const res = await webAPI.get(`/sp/event/${id}`, {});

    runInAction(() => {
      if (this.cards[idx]) this.cards[idx].data = { ...res.data };
    });
  }

  removeCard(id: string): void {
    const idx = this.cards.findIndex((el) => String(el.key) === id);

    if (idx !== -1) {
      if (id === String(this.activeTab)) {
        this.activeTab = "";
      }
      this.cards.splice(idx, 1);
    }
    const path = this.activeTab.replace(/[P,D]/, "");
    const isNoCardTab = [
      REGISTER_DEFECTS,
      REGISTER_PLANNED,
      CONSISTENT_DEFECTS,
      AGREED_PLANNED,
    ].includes(id);
    if (!isNoCardTab) {
      history.replace(`/sp/reestr/${path}`);
    }
  }

  async getNotificationSettings(id: string) {
    this.defectNotifySettingsAreLoading.setTrue();
    const res = await webAPI.get(
      `/sp/NotificationEvent/NotificationData/${id}`,
      {},
    );

    runInAction(() => {
      this.defectNotifySettings = res.data;
      this.defectNotifySettingsAreLoading.setFalse();
    });
  }

  async getNotificationTemplateText(eventId: string, templateId: string) {
    const res = await webAPI.get(
      `/sp/NotificationEvent/NotificationData/${eventId}/Text/${templateId}`,
      {},
    );

    runInAction(() => {
      this.defectNotifyText = res.data.item1;
      this.defectNotifyTextShort = res.data.item2;
    });
  }

  async sendDefectNotification(eventId: string, values) {
    this.defectNotifyFormIsSending.setTrue();
    this.defectNotifyStatus.resetStatus();

    try {
      const res = await webAPI.post(
        `/sp/NotificationEvent/Send/${eventId}`,
        values,
      );
      runInAction(() => {
        const fileName = res.headers["content-disposition"]
          .split("filename=")[1]
          .split(";")[0];
        downloadRawFileFromServer(res.data, fileName);
        this.defectNotifyStatus.setStatus(Constants.requestStatuses.notifySend);
        this.defectNotifyFormIsOpened.setFalse();
        this.editCard(Number(eventId));
        this.allDefectData.allData();
        this.agreementDefectData.allData();
        this.plannedDefectData.allData();
        this.agreementPlanedDefectData.allData();
      });
      if (res) {
        this.filterDefectsIds(eventId);
      }
    } catch (e) {
      this.defectNotifyStatus.setStatus(Constants.requestStatuses.error);
      console.log("Ошибка");
    } finally {
      this.defectNotifyFormIsSending.setFalse();
    }
  }

  closeNotifyForm() {
    this.defectNotifyFormIsOpened.setFalse();
    this.defectNotifyStatus.resetStatus();
  }

  async sendDefectRejection(defectId, values) {
    this.defectRejectFormIsSending.setTrue();
    this.defectRejectStatus.resetStatus();

    try {
      await webAPI.post(
        `/sp/DefectVerification/${defectId}/RejectDefect`,
        values,
      );

      runInAction(() => {
        this.defectRejectStatus.setStatus(Constants.requestStatuses.rejectSend);
        this.defectRejectFormIsOpened.setFalse();
        this.editCard(Number(defectId));
        this.allDefectData.allData();
        this.agreementDefectData.allData();
        this.plannedDefectData.allData();
        this.agreementPlanedDefectData.allData();
      });
    } catch (e) {
      this.defectRejectStatus.setStatus(Constants.requestStatuses.error);
    } finally {
      this.defectRejectFormIsSending.setFalse();
    }
  }

  closeRejectForm() {
    this.defectRejectFormIsOpened.setFalse();
    this.defectRejectStatus.resetStatus();
  }

  async sendDefectRemove(defectId, values, defectCardTabKey) {
    this.defectRemoveFormIsSending.setTrue();
    this.defectRemoveStatus.resetStatus();

    try {
      await webAPI.post(`/sp/Event/${defectId}/SetDeleted`, values);

      runInAction(() => {
        this.defectRemoveStatus.setStatus(Constants.requestStatuses.removeSend);
        this.allDefectData.allData();
        this.agreementDefectData.allData();
        this.plannedDefectData.allData();
        this.agreementPlanedDefectData.allData();
        this.removeCard(defectCardTabKey);
        this.editCard(Number(defectId)); // Не понятно зачем???
      });
    } catch (e) {
      this.defectRemoveStatus.setDetails(e);
      this.defectRemoveStatus.setStatus(Constants.requestStatuses.error);
      this.defectRemoveFormIsSending.setFalse();
    } finally {
      this.defectRemoveFormIsOpened.setFalse();
      this.defectRemoveFormIsSending.setFalse();
    }
  }

  closeRemoveForm() {
    this.defectRemoveFormIsOpened.setFalse();
    this.defectRemoveStatus.resetStatus();
  }

  get plannedCards() {
    return this.cards.filter((card) => card.planned);
  }

  get nonPlannedCards() {
    return this.cards.filter((card) => !card.planned);
  }

  adjustTime(data) {
    if (data.timeDisable) {
      data.timeDisable = timeToLocal(data.timeDisable);
    }
    if (data.timeEnable) {
      data.timeEnable = timeToLocal(data.timeEnable);
    }
    if (data.gasPlannedTime) {
      data.gasPlannedTime = timeToLocal(data.gasPlannedTime);
    }
  }

  async getDefectInfo(defectId) {
    if (!Number(defectId)) {
      this.defectInfoIsLoading.setFalse();
      this.defectInfo = null;
      return;
    }

    this.defectInfoIsLoading.setTrue();

    try {
      const res = await DefectFormApi.getIncident(defectId);

      runInAction(() => {
        const dataMkaDto = res.data.mkaDto;

        this.adjustTime(res.data);

        this.defectInfo = Object.assign(res.data, dataMkaDto);

        this.defectInfo.filesIds = this.defectInfo.files.map((file) => ({
          id: file.id,
        }));

        this.defectInfo.files = this.defectInfo.files.map((file, index) => {
          return {
            ...file,
            key: file.guid,
            deleted: false,
            loading: true,
          };
        });
      });

      this.defectInfoIsLoading.setFalse();
      await this.getDefectFiles();
    } catch (e) {
      console.log(e);
    } finally {
      this.defectInfoIsLoading.setFalse();
    }
  }
  async getDefectInfoAdded(defectId) {
    return DefectFormApi.getIncident(defectId);
  }

  async getDefectFiles() {
    const urls = this.defectInfo.files?.map((file) => file?.guid);
    const req = urls?.map((url) => FileApi.getFileByGuid(url));
    const resFiles = await Promise.allSettled(req);

    const urlCreator = window.URL || window.webkitURL;

    this.defectInfo.files = this.defectInfo.files.map((file, index) => {
      let thumbUrl = null;
      if (resFiles[index].status === "fulfilled") {
        thumbUrl = urlCreator.createObjectURL(resFiles[index].value?.data);
      }
      return {
        ...file,
        key: file.guid,
        deleted: false,
        loading: false,
        thumbUrl,
      };
    });
  }

  async saveDefect(data, id = 0, history) {
    //debugger;
    this.defectFormIsSending.setTrue();
    this.saveDefectStatus.resetStatus();

    if (id) {
      DefectFormApi.updateIncident(data, id)
        .then((res) => {
          this.defectFormIsSending.setFalse();
          this.saveDefectStatus.setStatus(Constants.requestStatuses.updated);
          this.editCard(id).then(() => {
            history.push(`/sp/reestr`);
          });
        })
        .catch((err) => {
          const [error] = err?.response?.data?.errors ?? [];
          console.error("Ошибка обновления дефекта", err);
          message.error(error || "Ошибка обновления дефекта");
          this.defectFormIsSending.setFalse();
        });
    } else {
      DefectFormApi.createIncident(data)
        .then((res) => {
          this.defectFormIsSending.setFalse();
          this.saveDefectStatus.setStatus(Constants.requestStatuses.created);
          this.editCard(Number(id)).then(() => {
            history.push(`/sp/reestr`);
          });
        })
        .catch((err) => {
          const [error] = err?.response?.data?.errors ?? [];
          console.error("Ошибка создания дефекта", err);
          message.error(error || "Ошибка создания дефекта");
          this.defectFormIsSending.setFalse();
        });
    }
    // const res = !id
    //   ? await DefectFormApi.createIncident(data)
    //   : await DefectFormApi.updateIncident(data, id);
    //   debugger;
    // this.defectFormIsSending.setFalse();
    // this.saveDefectStatus.setStatus(Constants.requestStatuses.created);

    // await this.editCard(Number(id));
    // history.push(`/sp/reestr`);
    // } catch (e) {
    //   debugger;
    //   this.saveDefectStatus.setDetails(e);
    //   this.saveDefectStatus.setStatus(Constants.requestStatuses.error);
    //   if (e.response.data.errors?.length > 0) {
    //     const error = e.response.data.errors[0];
    //     this.saveDefectError = error?.split(":").slice(1).join("");
    //     this.saveDefectFileIsOpened.setTrue();
    //   }
    // } finally {
    //   debugger;
    //   this.defectFormIsSending.setFalse();
    // }
  }

  closeSaveDefect() {
    this.saveDefectFileIsOpened.setFalse();
    this.saveDefectStatus.resetStatus();
  }

  async getNotificationsLists(defectId) {
    this.notificationsListsAreLoading.setTrue();

    try {
      const res = await webAPI.post("/sp/NotificationEvent/Notifications", {
        page: this.notificationsListsDataPaginator.page,
        count: this.notificationsListsDataPaginator.perPage,
        id: defectId,
      });

      runInAction(() => {
        const notificationData =
          res.data?.items?.filter((el) => el.eventId === defectId) || [];
        this.notificationsListsData = notificationData;
        this.notificationsListsDataPaginator.updateMeta(res.data);
        this.notificationsListsAreLoading.setFalse();
        this.editCard(Number(defectId));
      });
    } catch (e) {
      console.log(e);
    } finally {
      this.notificationsListsAreLoading.setFalse();
    }
  }

  async sendDefectToMka(id, values) {
    this.defectToMkaIsSending.setTrue();
    this.sendDefectStatus.resetStatus();

    try {
      await webAPI.post(`/sp/Event/SendToMka`, values);

      message.success("Дефект отправлен в ПППУР.");

      runInAction(() => {
        this.sendDefectStatus.setStatus(Constants.statusMessage.created);
        this.editCard(Number(id));
        this.allDefectData.allData();
        this.agreementDefectData.allData();
        this.plannedDefectData.allData();
        this.agreementPlanedDefectData.allData();
      });
    } catch (e) {
      this.defectToMkaError = this.sendDefectStatus.getDetailsFromError(e);
      this.sendDefectStatus.setDetails(e);
      this.sendDefectStatus.setStatus(Constants.requestStatuses.error);
      this.sendDefectToMkaIsOpened.setTrue();
      await this.editCard(Number(id));
    } finally {
      this.defectToMkaIsSending.setFalse();
    }
  }

  closeDefectToMkaForm() {
    this.sendDefectToMkaIsOpened.setFalse();
    this.sendDefectStatus.resetStatus();
  }

  setCardIsLoading(id: any, isloading: boolean) {
    const idx = this.cards.findIndex((el) => el.data.id == id);
    if (this.cards[idx]) this.cards[idx].isLoading = isloading;
  }

  async sendDefectAgreed(
    id: string | number,
    values: { ids: string[] | number[]; isValid: boolean },
    cardkey: string = "",
  ) {
    // подтверждение дефекта
    this.defectAgreedIsSending.setTrue();
    this.sendDefectStatus.resetStatus();

    this.checkDefectsAgreedIds(values);

    DispatchApi.updateStatus(values)
      .then(() => {
        this.defectAgreedIsSending.setFalse();
        runInAction(() => {
          this.sendDefectStatus.setStatus(Constants.statusMessage.updated);
          this.editCard(Number(id)).then(() => {
            this.setCardIsLoading(id, false);
            if (cardkey !== "")
              setTimeout(() => {
                this.removeCard(cardkey);
              }, 2000);
          });
          this.allDefectData.allData();
          this.agreementDefectData.allData();
          this.plannedDefectData.allData();
          this.agreementPlanedDefectData.allData();
        });
      })
      .catch((e) => {
        this.sendDefectStatus.setDetails(e);
        this.sendDefectStatus.setStatus(Constants.requestStatuses.error);
        this.defectAgreedIsSending.setFalse();
        this.setCardIsLoading(id, false);
      });
  }

  async sendDefectAgreedAll(values: {
    ids: string[] | number[];
    isValid: boolean;
  }) {
    // подтверждение нескольких
    this.defectAgreedIsSending.setTrue();
    this.sendDefectStatus.resetStatus();

    this.checkDefectsAgreedIds(values);

    try {
      const res = await DispatchApi.updateStatus(values);

      runInAction(() => {
        this.sendDefectStatus.setStatus(Constants.statusMessage.updated);
        this.allDefectData.allData();
        this.agreementDefectData.allData();
        this.plannedDefectData.allData();
        this.agreementPlanedDefectData.allData();
      });
      // if (res) {
      //   this.checkDefectsAgreedIds(values);
      // }
    } catch (e) {
      this.sendDefectStatus.setStatus(Constants.requestStatuses.error);
    } finally {
      this.defectAgreedIsSending.setFalse();
    }
  }

  async getDefectUploadFile(guid: string) {
    //this.defectUploadFileAreLoading.setTrue();
    const res = await FileApi.getFileByGuid(guid);

    runInAction(() => {
      const urlCreator = window.URL || window.webkitURL;
      this.defectUploadFiles.push(urlCreator.createObjectURL(res.data));

      //this.defectUploadFileAreLoading.setFalse();
    });
  }

  checkDefectsAgreedIds = async ({ ids }: { ids: string[] | number[] }) => {
    // проверка статуса id дефектов
    this.sendDefectStatus.resetStatus();
    try {
      const stringIds = ids?.map((id) => `searchIds=${id}`).join("&");
      const { data } = await webAPI.get(
        `/sp/Event/MajorAccidents?${stringIds}`,
      );
      if (data) {
        this.setDefectsIds(data);
      }
    } catch (e) {
      this.sendDefectStatus.setStatus(Constants.requestStatuses.error);
      this.clearDefectsIds();
    }
  };
  setDefectsIds = (ids: string[] | number[] | string | number) => {
    if (Array.isArray(ids)) {
      this.defectsIds = [...this.defectsIds, ...ids];
    } else {
      this.defectsIds = [...this.defectsIds, ids];
    }
  };
  filterDefectsIds = (id: string) => {
    this.defectsIds = this.defectsIds.filter((defId) => defId !== id);
  };
  clearDefectsIds = () => {
    this.defectsIds = [];
  };
}

const defectCardsStore = new DefectCards();
export default defectCardsStore;
